import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import QuickSelector from './components/QuickSelector';
import SpecificationDetails from './components/SpecificationDetails';
import Footer from '../home/components/Footer';

export default function ProductSpecifications() {
  const [selectedModel, setSelectedModel] = useState('a-solo');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="Modular Pod Specifications | Nexa Pods Australia"
        description="Compare dimensions, layouts and technical specifications for Nexa Pods modular living, office and accommodation models available in Australia."
        canonicalPath="/product-specifications"
        keywords="modular pod specifications Australia, Nexa Pods dimensions, modular cabin layouts, pod technical details"
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/images/c719b13a28cc.webp" 
              alt="Space Capsule Living" 
              className="h-16 sm:h-20 lg:h-28 w-auto"
            />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Products Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link 
                to="/product-range"
                className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Products
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-48">
                  <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer"
                    >
                      A Series
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer"
                    >
                      Z Series
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="/#features" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Features</a>
            <a href="/#interior" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Interior</a>
            <a href="/#specs" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Specifications</a>
            <a href="/#investment" className="text-base text-gray-300 hover:text-cyan-400 transition-colors whitespace-nowrap cursor-pointer">Investment</a>
            <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
            <div className="px-4 py-6 space-y-4 w-full">
              {/* Mobile Products Dropdown */}
              <div>
                <button 
                  onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                  className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {productsDropdownOpen && (
                  <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer"
                    >
                      A Series
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer"
                    >
                      Z Series
                    </Link>
                  </div>
                )}
              </div>

              <a href="/#features" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Features</a>
              <a href="/#interior" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Interior</a>
              <a href="/#specs" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Specifications</a>
              <a href="/#investment" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Investment</a>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative pt-40 sm:pt-44 md:pt-48 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,217,255,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(0,217,255,0.05),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 bg-cyan-500/10 border border-cyan-400/30 rounded-full mb-6 sm:mb-8">
            <i className="ri-file-list-3-line text-cyan-400 text-base sm:text-lg"></i>
            <span className="text-cyan-400 text-xs sm:text-sm font-medium">Technical Documentation</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 px-4">
            Product Specifications
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Detailed technical specifications and features for all Space Capsule models
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 md:pb-24">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar - Desktop Sticky, Mobile Top */}
          <div className="lg:col-span-3 w-full">
            <QuickSelector 
              selectedModel={selectedModel}
              onSelectModel={setSelectedModel}
            />
          </div>

          {/* Details Section */}
          <div className="lg:col-span-9 w-full">
            <SpecificationDetails selectedModel={selectedModel} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
      </div>
    </>
  );
}
